import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Band,
  Button,
  Eyebrow,
  Hero,
  Prose,
  Split,
  Wrap,
  heroActions,
  sectionHead,
} from "../components/ui/index.ts";

/**
 * The company's front door.
 *
 * Everything here has to stay true if the press publishes something that is not
 * a logic grid. So the standard on this page is "we check what we print", and
 * the narrower promises — one solution, no guessing, minimal clues — live on
 * pages/LogicGrids.tsx where they belong to a line rather than to the company.
 * The catalogue is kept off this page too: a book list grows, and a growing
 * list would slowly turn the company back into its first product line.
 *
 * Puzzle help used to live here as a section, with `/help` rendering this page
 * scrolled to it. It is pages/Help.tsx now, so the code printed in a book lands
 * on a page about the reader problem rather than on the company pitch.
 */
export default function Home() {
  const { hash } = useLocation();

  useEffect(() => {
    const id = hash.slice(1);
    if (!id) return;

    // One scroll on mount does not stick. Several things run after a React
    // effect on a cold load and each one undoes it: the browser's own scroll
    // restoration, the webfonts landing and re-flowing everything below them,
    // and the emblem sizing itself. So keep asking until the section is where
    // it should be, and give up after a second rather than looping forever.
    // `scroll-margin-top` on the section keeps the sticky masthead off the
    // heading we land on.
    let tries = 0;
    const settle = () => {
      const el = document.getElementById(id);
      if (!el) return true;
      const { top } = el.getBoundingClientRect();
      const target = Number.parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
      if (Math.abs(top - target) < 4) return true;
      el.scrollIntoView({ block: "start" });
      return ++tries >= 10;
    };

    if (settle()) return;
    const timer = setInterval(() => {
      if (settle()) clearInterval(timer);
    }, 100);
    return () => clearInterval(timer);
  }, [hash]);

  return (
    <>
      <Hero
        eyebrow="Independent · Est. 2026"
        title={
          <>
            We only print
            <br />
            what we can <em className="italic text-rust">prove</em>.
          </>
        }
        lede="We're a small independent press making logic grid puzzle books — the kind where the puzzles actually work."
        ledeLarge
        largeTitle
        emblem="default"
        actions={
          <>
            <Button to="/catalogue">See the catalogue</Button>
            <Button to="/help" ghost>
              Stuck on a puzzle?
            </Button>
          </>
        }
      />

      <Band id="standard">
        <Wrap>
          <Eyebrow>The house standard</Eyebrow>
          <h2 className={sectionHead}>We check what we print.</h2>
          <Prose>
            <p className="lead-para">
              Every puzzle in every book gets solved by machine before we print it. If it turns out
              to have two answers, or to need a lucky guess halfway through, we throw it out and
              make another one.
            </p>
            <p>
              It's a slow way to run a press, and it means we publish less than we could. But when
              we tell you what's inside a book, we're reading off a test result rather than hoping.
            </p>
          </Prose>
        </Wrap>
      </Band>

      <Band variant="alt" id="publishing">
        <Wrap>
          <Eyebrow>What we publish</Eyebrow>
          <Split>
            <Prose>
              <h2 className={sectionHead}>Logic grid puzzles, for now.</h2>
              <p>
                You get a few people, some things to match them to, a list of clues, and a grid to
                work it out on. Each book stays at one difficulty the whole way through, so you know
                what you're picking up.
              </p>
              <p className={heroActions}>
                <Button to="/logic-grids">What we guarantee</Button>
                <Button to="/catalogue" ghost>
                  Browse the books
                </Button>
              </p>
            </Prose>
            <Prose>
              <h2 className={sectionHead}>And after that?</h2>
              <p>
                More puzzle books for a while. After that, anything else we can check just as
                thoroughly, which honestly rules out most things.
              </p>
              <p>We're not trying to have a big catalogue.</p>
            </Prose>
          </Split>
        </Wrap>
      </Band>

      <Band id="about">
        <Wrap>
          <Eyebrow>Say hello</Eyebrow>
          <h2 className={sectionHead}>A small press, run by people.</h2>
          <Prose>
            <p>
              We're small on purpose, and we'd like to stay that way. If you've found a mistake,
              want a book we don't make yet, or just want to say hello, email us. We read
              everything.
            </p>
            <p>
              <Button href="mailto:hello@wisehyena.com">hello@wisehyena.com</Button>
            </p>
          </Prose>
        </Wrap>
      </Band>
    </>
  );
}
